import React, { useState, useEffect } from "react";

const TableManagerModal = ({ onClose, onTableChanged }) => {
  const [tables, setTables] = useState([]);
  const [tableTypes, setTableTypes] = useState([]);
  const [orientations, setOrientations] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [tableSearch, setTableSearch] = useState("");

  const [newTable, setNewTable] = useState({
    label: "",
    type: "square",
    seats: 4,
    orientation: "horizontal",
    is_outdoor: false,
  });

  useEffect(() => {
    if (tableTypes.length > 0) {
      const selectedType = tableTypes.find(t => t.value === newTable.type);
      if (selectedType?.default_seats) {
        setNewTable(prev => ({ ...prev, seats: selectedType.default_seats }));
      }
    }
  }, [newTable.type, tableTypes]);

  useEffect(() => {
    fetchTables();
    fetch("/reservations/api/tables/types/")
      .then(res => res.json())
      .then(data => {
        setTableTypes(data.types || []);
        setOrientations(data.orientations || []);
      });
  }, []);

  const fetchTables = () => {
    fetch("/reservations/api/tables/")
      .then(res => res.json())
      .then(data => setTables(data.tables || []));
  };

  const handleCreate = async () => {
    setError("");
    setSuccess("");

    if (!newTable.label.trim()) {
      setError("Oznaka stola je obavezna.");
      return;
    }

    const response = await fetch("/reservations/api/tables/create/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newTable),
    });

    const data = await response.json();
    if (response.ok) {
      setSuccess(`Stol '${newTable.label}' uspješno dodan!`);
      setNewTable({ label: "", type: "square", seats: 4, orientation: "horizontal", is_outdoor: false });
      fetchTables();
      onTableChanged();
    } else {
      setError(data.error || "Greška pri dodavanju stola.");
    }
  };

  const handleDelete = async (id) => {
    const response = await fetch(`/reservations/api/tables/${id}/delete/`, { method: "DELETE" });
    const data = await response.json();
    if (response.ok) {
      setSuccess(data.message);
      fetchTables();
      onTableChanged();
    } else {
      setError(data.error || "Greška pri brisanju.");
    }
    setConfirmDelete(null);
  };

  const needsOrientation = ["rect_4", "rect_6"].includes(newTable.type);

  const filteredTables = tables.filter(t =>
    t.label.toLowerCase().includes(tableSearch.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-[#c1b428] rounded-xl shadow-2xl w-[600px] max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="p-4 border-b border-[#c1b428]">
          <h2 className="text-base font-bold text-center text-[#c1b428]">
            Upravljanje stolovima
          </h2>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-4">

          {/* Poruke */}
          {error && (
            <div className="p-2 bg-red-900 border border-[#c1b428] rounded text-[#c1b428] text-xs">
              {error}
            </div>
          )}
          {success && (
            <div className="p-2 bg-green-900 border border-green-500 rounded text-green-400 text-xs">
              {success}
            </div>
          )}

          {/* Forma za novi stol */}
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <h3 className="text-[#c1b428] font-semibold text-xs mb-3">➕ Dodaj novi stol</h3>
            <div className="grid grid-cols-2 gap-2">

              <div>
                <label className="text-[#c1b428] text-xs block mb-1">Oznaka stola:</label>
                <input
                  type="text"
                  value={newTable.label}
                  onChange={(e) => setNewTable({ ...newTable, label: e.target.value })}
                  placeholder="npr. T15"
                  className="w-full bg-gray-700 text-white border border-gray-600 rounded px-2 py-1 text-xs"
                />
              </div>

              <div>
                <label className="text-[#c1b428] text-xs block mb-1">Tip stola:</label>
                <select
                  value={newTable.type}
                  onChange={(e) => setNewTable({ ...newTable, type: e.target.value })}
                  className="w-full bg-gray-700 text-[#c1b428] border border-gray-600 rounded px-2 py-1 text-xs"
                >
                  {tableTypes.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[#c1b428] text-xs block mb-1">Broj mjesta:</label>
                <input
                  type="number"
                  value={newTable.seats}
                  onChange={(e) => setNewTable({ ...newTable, seats: Number(e.target.value) })}
                  min="1"
                  max="20"
                  className="w-full bg-gray-700 text-white border border-gray-600 rounded px-2 py-1 text-xs"
                />
              </div>

              {needsOrientation && (
                <div>
                  <label className="text-[#c1b428] text-xs block mb-1">Orijentacija:</label>
                  <select
                    value={newTable.orientation}
                    onChange={(e) => setNewTable({ ...newTable, orientation: e.target.value })}
                    className="w-full bg-gray-700 text-[#c1b428] border border-gray-600 rounded px-2 py-1 text-xs"
                  >
                    {orientations.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center gap-2 mt-1 col-span-2">
                <input
                  type="checkbox"
                  id="is_outdoor"
                  checked={newTable.is_outdoor}
                  onChange={(e) => setNewTable({ ...newTable, is_outdoor: e.target.checked })}
                  className="accent-[#c1b428]"
                />
                <label htmlFor="is_outdoor" className="text-[#c1b428] text-xs">
                  Vanjski stol (terasa)
                </label>
              </div>
            </div>

            <button
              onClick={handleCreate}
              className="mt-3 w-full px-3 py-1.5 bg-[#c1b428] text-black rounded font-bold hover:bg-yellow-500 text-xs"
            >
              Dodaj stol
            </button>
          </div>

          <div className="relative mb-2">
          <input
              type="text"
              placeholder="Pretraži stol..."
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              className="w-full bg-gray-700 text-white border border-gray-600 rounded px-3 py-1.5 text-xs pr-6"
          />
          {tableSearch && (
              <button
              onClick={() => setTableSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-xs"
              >
              ✕
              </button>
          )}
          </div>          

          {/* Lista stolova */}
          <div>
            <h3 className="text-[#c1b428] font-semibold text-xs mb-2">
              Postojeći stolovi ({filteredTables.length})
            </h3>
            <div className="space-y-1">
              {filteredTables.map(t => (
                <div
                  key={t.id}
                  className="flex justify-between items-center bg-gray-800 border border-gray-700 rounded px-3 py-2 text-xs"
                >
                  <div className="flex items-center gap-3">
                    <span className="bg-[#c1b428] text-black font-bold px-2 py-0.5 rounded text-xs">
                      {t.label}
                    </span>
                    <span className="text-gray-300">
                      {tableTypes.find(tp => tp.value === t.type)?.label || t.type}
                    </span>
                    <span className="text-gray-400">👥 {t.seats}</span>
                    {t.is_outdoor && <span className="text-green-400">🌿 Terasa</span>}
                  </div>
                  <button
                    onClick={() => setConfirmDelete(t)}
                    className="px-2 py-1 bg-gray-700 text-red-400 rounded hover:bg-red-900 text-xs"
                    title="Obriši stol"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-[#c1b428] flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-gray-700 text-[#c1b428] rounded hover:bg-gray-600 text-xs font-semibold"
          >
            Zatvori
          </button>
        </div>
      </div>

      {/* Confirm delete dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
          <div className="bg-gray-900 border border-[#c1b428] rounded-xl p-6 w-80 text-center">
            <div className="text-3xl mb-3">🗑️</div>
            <h3 className="text-[#c1b428] font-bold text-sm mb-2">Brisanje stola</h3>
            <p className="text-gray-300 text-xs mb-2">
              Jesi li siguran da želiš obrisati stol{" "}
              <span className="text-[#c1b428] font-bold">{confirmDelete.label}</span>?
            </p>
            <p className="text-red-400 text-xs mb-5">
              ⚠️ Sve rezervacije za ovaj stol će biti obrisane!
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 bg-gray-700 text-[#c1b428] rounded hover:bg-gray-600 text-xs font-semibold"
              >
                Odustani
              </button>
              <button
                onClick={() => handleDelete(confirmDelete.id)}
                className="px-4 py-2 bg-red-700 text-white rounded hover:bg-red-600 text-xs font-bold"
              >
                Obriši
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableManagerModal;